import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { AvisoInstancia } from '@/components/AvisoInstancia';
import { FormularioIngreso } from '@/components/FormularioIngreso';
import { MarcaBetLink, PieCoopen } from '@/components/MarcaBet';
import { instanciaActual } from '@/lib/instancia';

/**
 * Pantalla de ingreso.
 *
 * La instancia de PRODUCCIÓN (cuenta de BET) tiene la tapa del libro; la vieja
 * (cuenta de carlos) queda sobria, con su cartel de mudanza. Que se vean
 * distintas es el punto: hasta el 2026-09-18 eran idénticas y no había forma de
 * saber en cuál se estaba entrando.
 */
export default function LoginPage() {
  return instanciaActual().esProduccion ? <IngresoLibro /> : <IngresoMudanza />;
}

/* ── Producción: "libro contable encuadernado" ───────────────────────────── */

function saludo(hora: number) {
  if (hora < 12) return 'Buenos días';
  if (hora < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

function IngresoLibro() {
  // La fecha del día escrita como encabezado de libro: "jueves, 18 de septiembre".
  // Se calcula una vez por visita — nadie deja el login abierto de un día al otro.
  const { fecha, hola } = useMemo(() => {
    const ahora = new Date();
    const f = new Intl.DateTimeFormat('es-CO', {
      weekday: 'long', day: 'numeric', month: 'long',
    }).format(ahora);
    return { fecha: f.charAt(0).toUpperCase() + f.slice(1), hola: saludo(ahora.getHours()) };
  }, []);

  return (
    <div className="fuente-texto grid min-h-screen bg-stone-50 lg:grid-cols-[1.05fr_1fr]">
      {/* La tapa */}
      <section className="ingreso-tapa relative flex flex-col px-8 py-6 pl-[84px] text-[hsl(var(--libro-crema))] sm:px-12 sm:pl-[92px] lg:py-10">
        <MarcaBetLink className="ingreso-paso -ml-1 self-start text-[hsl(var(--libro-crema))]" />

        <div className="flex flex-1 flex-col justify-center py-10 lg:py-0">
          <p
            className="ingreso-paso text-sm font-medium tracking-wide text-primary tabular-nums"
            style={{ '--paso': 1 } as React.CSSProperties}
          >
            {fecha}
          </p>
          <h1
            className="ingreso-paso fuente-display mt-3 text-5xl font-normal leading-[1.05] tracking-tight sm:text-6xl"
            style={{ '--paso': 2 } as React.CSSProperties}
          >
            {hola}.
          </h1>
          <p
            className="ingreso-paso mt-5 max-w-[34ch] text-base leading-8 text-[hsl(var(--libro-crema)/0.72)] [text-wrap:pretty] sm:text-lg"
            style={{ '--paso': 3 } as React.CSSProperties}
          >
            Las facturas del día ya están listas para su revisión.
          </p>
        </div>

        <p className="hidden text-xs tracking-wide text-[hsl(var(--libro-crema)/0.4)] lg:block">
          Aprobación de facturas · uso interno
        </p>
      </section>

      {/* La hoja */}
      <section className="flex flex-col">
        <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-10">
          <div
            className="ingreso-paso w-full max-w-sm"
            style={{ '--paso': 4 } as React.CSSProperties}
          >
            <h2 className="fuente-display text-2xl font-semibold tracking-tight text-stone-900">
              Ingrese a su cuenta
            </h2>
            <p className="mb-8 mt-1.5 text-sm text-stone-500">
              Con el correo de la empresa.
            </p>
            <FormularioIngreso />
          </div>
        </div>
        <PieCoopen />
      </section>
    </div>
  );
}

/* ── La instancia vieja: sobria, con el cartel de mudanza ────────────────── */

function IngresoMudanza() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <AvisoInstancia />
          <FormularioIngreso />
        </CardContent>
      </Card>
    </div>
  );
}
